import React from 'react';
import { Box, IconButton, ThemeProvider, useTheme } from '@mui/material';
import { Logout, OpenInNew } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { isUserAdmin } from '../utils/auth';
import { COLORS, PAGE } from '../theme';
import { adminDarkTheme, adminLightTheme } from './admin/adminTheme';
import AdminSidebarBrand from './admin/AdminSidebarBrand';
import AdminMobileNav from './admin/AdminMobileNav';
import PageHeader from './shared/PageHeader';
import PageContainer from './shared/PageContainer';
import NotificationBell from './notificacoes/NotificationBell';
import ThemeToggle from './ThemeToggle';
import logoBranco from '../assets/logos/Logo Branco com Laranja.svg';
import { Sparkle } from './landing/LandingShapes';
import { encerrarSessao } from '../utils/logout';
import useIsMobile from '../hooks/useIsMobile';
import useNavegacaoSwipeAdmin from '../hooks/useNavegacaoSwipeAdmin';

// Cabeçalhos automáticos para páginas que ainda não renderizam o `PageHeader`
// por conta própria. Só o título é resolvido aqui: rubrica, ícone e subtítulo
// vêm do `HEADER_META` do próprio `PageHeader` (fonte única da identidade).
const getAutoHeader = (pathname) => {
  if (pathname.startsWith('/editar-pedido/')) {
    return { title: 'Editar Pedido' };
  }
  if (pathname.startsWith('/editar-cliente/')) {
    return { title: 'Editar Cliente' };
  }
  if (pathname.startsWith('/dados-cliente/')) {
    return { title: 'Dados do Cliente' };
  }
  if (pathname.startsWith('/cadastros-pendentes/editar/')) {
    return { title: 'Editar Cadastro Pendente' };
  }
  if (pathname === '/indicacoes') {
    return { title: 'Indicações' };
  }
  if (pathname === '/instagram-ai') {
    return { title: 'Instagram IA' };
  }
  return null;
};

const Layout = ({ children }) => {
  const admin = isUserAdmin();
  const outerTheme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const mode = outerTheme.palette.mode === 'dark' ? 'dark' : 'light';
  // O header mobile aparece abaixo de `md`; o swipe entre as páginas do topo
  // vale exatamente na mesma faixa (no desktop a sidebar já resolve).
  const isMobile = useIsMobile('md');
  const swipe = useNavegacaoSwipeAdmin({ ativo: isMobile });
  const autoHeader = getAutoHeader(location.pathname);

  const handleLogout = async () => {
    await encerrarSessao();
    navigate('/');
  };

  if (!admin) {
    return (
      <ThemeProvider theme={mode === 'dark' ? adminDarkTheme : adminLightTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
          {/* Mesmo padrão de largura: quem cai aqui (sem sidebar) também herda
              a caixa única das páginas. */}
          <PageContainer>{children}</PageContainer>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={mode === 'dark' ? adminDarkTheme : adminLightTheme}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100%',
          // Impede que qualquer linha do menu mobile crie rolagem horizontal
          // e deixe uma faixa vazia à direita do conteúdo.
          overflowX: 'clip',
          bgcolor: 'background.default',
          color: 'text.primary',
        }}
      >
        <AdminSidebarBrand />

        {/* Menu no topo no mobile */}
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden',
            zIndex: 1300,
            bgcolor: 'rgba(28,27,25,0.98)',
            color: COLORS.cream,
            borderBottom: '1px solid rgba(242,238,231,0.12)',
            pt: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => navigate('/dashboard')}
              aria-label="Ir para o dashboard"
              sx={{
                border: 0,
                bgcolor: 'transparent',
                p: 0,
                cursor: 'pointer',
                display: 'flex',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logoBranco}
                alt="Zoompro Studio"
                sx={{ height: 26, maxWidth: '100%' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
              <NotificationBell color={COLORS.cream} centralPath="/notificacoes" />
              <Box sx={{ '& .MuiIconButton-root': { color: 'rgba(242,238,231,0.8) !important' } }}>
                <ThemeToggle />
              </Box>
              <IconButton
                onClick={() => window.open('/', '_blank')}
                aria-label="Ver site"
                sx={{ color: COLORS.cream }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
              <IconButton onClick={handleLogout} aria-label="Sair" sx={{ color: COLORS.orange }}>
                <Logout fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <AdminMobileNav />
        </Box>
        <Box
          component="main"
          ref={swipe.ref}
          sx={{
            flexGrow: 1,
            width: { xs: '100%', md: 'auto' },
            maxWidth: '100%',
            minWidth: 0,
            overflowX: 'clip',
            ml: { xs: 0, md: '280px' },
            minHeight: '100vh',
            position: 'relative',
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 112px)', md: 0 },
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              left: { xs: 0, md: '280px' },
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            {/* A grade da identidade NÃO fica atrás do conteúdo (ela se
                misturava com os fios do dashboard). Ela segue viva dentro do
                `PageHeader` (hero) e da sidebar; aqui fica só o adorno. */}
            <Sparkle
              size={54}
              color={COLORS.orange}
              sx={{ position: 'absolute', top: 120, right: '4%', opacity: 0.14 }}
            />
          </Box>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              // Padding e largura da página vêm do padrão único (`PAGE`), não de
              // cada tela: aqui todas as rotas do painel herdam a mesma caixa.
              px: PAGE.paddingX,
              py: PAGE.paddingY,
            }}
          >
            <PageContainer>
              {autoHeader && <PageHeader title={autoHeader.title} />}
              {children}
            </PageContainer>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
